import { normalizeDurationOperator } from '@/data/Marketing/marketingDurationOperators';
import {
    convertHoursToDuration,
    type SelectedDurationFormat,
} from '@/redux/slices/Marketing/triggerFlow/triggerFlowDataUtils';
import { DEFAULT_DAY_SELECTOR_VALUE, type DaySelectorLabel, type DaySelectorValue } from './DaySelector';

/** When composite still holds API hours but format is set, convert for display. */
function resolveDisplayAmount(storedAmount: number, format: SelectedDurationFormat): number {
    if (!Number.isFinite(storedAmount) || storedAmount <= 0) {
        return storedAmount;
    }
    if (format === 'HOURS') {
        return storedAmount;
    }
    const thresholds: Record<Exclude<SelectedDurationFormat, 'HOURS'>, number> = {
        DAYS: 24,
        WEEKS: 168,
        MONTHS: 720,
    };
    if (storedAmount >= thresholds[format]) {
        return convertHoursToDuration(storedAmount, format);
    }
    return storedAmount;
}

export const DEFAULT_DURATION_AMOUNT = DEFAULT_DAY_SELECTOR_VALUE.hours;
export const DEFAULT_DURATION_FORMAT: DaySelectorLabel = DEFAULT_DAY_SELECTOR_VALUE.label;

/** Use with formik.setTouched when `duration` is a nested DaySelectorValue object. */
export const DURATION_HOURS_TOUCHED = { hours: true } as const;

export type DurationCompositeSource = {
    days?: [number | null, number | null];
    expirationPeriod?: [number | null, number | null];
    selectedDurationFormat?: DaySelectorLabel;
    operator?: string;
};

/** @deprecated Use DaySelectorValue */
export type DurationPeriodValue = {
    amount: number;
    format: DaySelectorLabel;
};

export function getDaySelectorValueFromComposite(
    composite?: DurationCompositeSource | null,
    options?: { includeOperator?: boolean; defaultOperator?: string },
): DaySelectorValue {
    const period = composite?.days ?? composite?.expirationPeriod;
    // When the composite carries an explicit format, the amount is already in
    // display units (the mapper / buildCompositeFromDaySelectorValue converted it).
    // Only legacy data without a format still holds raw API hours.
    const hasExplicitFormat = composite?.selectedDurationFormat != null;
    const format = composite?.selectedDurationFormat ?? DEFAULT_DAY_SELECTOR_VALUE.label;
    const rawAmount = period?.[0];
    const displayAmount =
        rawAmount !== null && rawAmount !== undefined
            ? hasExplicitFormat
                ? rawAmount
                : resolveDisplayAmount(rawAmount, format)
            : DEFAULT_DAY_SELECTOR_VALUE.hours;
    return {
        hours: displayAmount,
        label: format,
        ...(options?.includeOperator
            ? {
                  operator: normalizeDurationOperator(
                      composite?.operator ?? options.defaultOperator ?? DEFAULT_DAY_SELECTOR_VALUE.operator,
                  ),
              }
            : {}),
    };
}

/** @deprecated Use getDaySelectorValueFromComposite */
export function getDurationFromComposite(composite?: DurationCompositeSource | null): DurationPeriodValue {
    const value = getDaySelectorValueFromComposite(composite);
    return { amount: value.hours, format: value.label };
}

export function buildCompositeFromDaySelectorValue(
    value: DaySelectorValue,
    field: 'days' | 'expirationPeriod' = 'days',
): DurationCompositeSource & { selectedDurationFormat: DaySelectorLabel } {
    const result: DurationCompositeSource & { selectedDurationFormat: DaySelectorLabel } = {
        [field]: [value.hours, null],
        selectedDurationFormat: value.label,
    };
    if (value.operator) {
        result.operator = normalizeDurationOperator(value.operator);
    }
    return result;
}

/** @deprecated Use buildCompositeFromDaySelectorValue */
export function buildDurationComposite(
    amount: number,
    format: DaySelectorLabel,
    field: 'days' | 'expirationPeriod' = 'days',
): DurationCompositeSource & { selectedDurationFormat: DaySelectorLabel } {
    return buildCompositeFromDaySelectorValue({ hours: amount, label: format }, field);
}
